import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Grid,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { getDateInputProps } from '../utils/dateConstraints';
import { DataGridPro } from '@mui/x-data-grid-pro';
import HoverExpandDataGridPro from '../components/HoverExpandDataGridPro';
import { useDataGridProTable, MuiXDataGridSurface } from '../hooks/useDataGridProTable';
import DashboardLayout from '../components/DashboardLayout';
import { useCachedData } from '../hooks/useCachedData';
import { endpoints } from '../api/config';
import {
  BUTTON_STYLE,
  DIALOG_CLOSE_BUTTON_STYLE,
  BUTTON_VARIANT,
  BUTTON_SIZE,
  NEAR_FULLSCREEN_DIALOG_PAPER_SX,
  COLUMN_MIN_SIZE,
  COLUMN_SIZE,
} from '../constants/styles';

// Helper function to format column header
const formatColumnHeader = (key) => {
  return key
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to flatten nested objects for detail view
const flattenObject = (obj, prefix = '') => {
  const flattened = {};
  
  Object.entries(obj).forEach(([key, value]) => {
    const newKey = prefix ? `${prefix}_${key}` : key;
    
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value, newKey));
    } else {
      flattened[newKey] = value;
    }
  });
  
  return flattened;
};

// Helper function to safely handle object values
const formatValue = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'object') {
    if (Array.isArray(value)) {
      return value.map(item => typeof item === 'object' ? JSON.stringify(item) : String(item)).join(', ');
    }
    return JSON.stringify(value);
  }

  return String(value);
};

const BinAdjustment = () => {
  // Default date range to today's date in YYYY-MM-DD
  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem('bin-adjustment-start-date');
    return saved || todayStr;
  });
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem('bin-adjustment-end-date');
    return saved || todayStr;
  });
  const [successFilter, setSuccessFilter] = useState(() => {
    const saved = localStorage.getItem('bin-adjustment-success-filter');
    return saved || 'all'; // all | success | errors
  });

  // Build endpoint with optional date range; by default this sends today's date range
  const binAdjustmentEndpointWithRange = useMemo(() => {
    const base = endpoints.binAdjustmentRecords;
    if (startDate && endDate) {
      return `${base}?start_date=${startDate}&end_date=${endDate}`;
    }
    if (startDate) {
      return `${base}?start_date=${startDate}`;
    }
    if (endDate) {
      return `${base}?end_date=${endDate}`;
    }
    return base;
  }, [startDate, endDate]);

  const { data, isLoading, error, refetch } = useCachedData(binAdjustmentEndpointWithRange);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedDetails, setSelectedDetails] = useState(null);

  const [itemDataOpen, setItemDataOpen] = useState(false);
  const [itemDataLoading, setItemDataLoading] = useState(false);
  const [itemDataDetails, setItemDataDetails] = useState(null);
  const [ackItemDetailsOpen, setAckItemDetailsOpen] = useState(false);
  const [ackItemDetails, setAckItemDetails] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Initial sorting state to show newest records first
  const [initialSorting] = useState([
    {
      id: 'id',
      desc: true
    }
  ]);
  
  // Default column visibility settings
  const defaultColumnVisibility = {
    id: true,
    username: true,
    timestamp: true,
    status: true,
    item_data: true,
    netsuite_response: true,
    actions: true,
    created_at: true,
    updated_at: true
  };

  const defaultDetailsColumnVisibility = {
    id: true,
    timestamp: true,
    status: true,
    item_data: true,
    netsuite_response: true,
    created_at: true,
    updated_at: true
  };

  const defaultItemDataColumnVisibility = {
    item_number: true,
    description: true,
    quantity: true,
    bin_number: true,
    location: true,
    created_at: true,
    updated_at: true
  };



  const defaultAckItemDetailsColumnVisibility = {
    item_id: true,
    item_name: true,
    quantity: true,
    bin_number: true,
    location: true,
    created_at: true,
    updated_at: true
  };

  // Main table column visibility
  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-adjustment-column-visibility');
    return saved ? JSON.parse(saved) : defaultColumnVisibility;
  });

  // Details table column visibility
  const [detailsColumnVisibility, setDetailsColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-adjustment-details-column-visibility');
    return saved ? JSON.parse(saved) : defaultDetailsColumnVisibility;
  });

  // Item data table column visibility
  const [itemDataColumnVisibility, setItemDataColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-adjustment-item-data-column-visibility');
    return saved ? JSON.parse(saved) : defaultItemDataColumnVisibility;
  });



  // Item Details from NetSuite Ack table column visibility
  const [ackItemDetailsColumnVisibility, setAckItemDetailsColumnVisibility] = useState(() => {
    const saved = localStorage.getItem('bin-adjustment-ack-item-details-column-visibility');
    return saved ? JSON.parse(saved) : defaultAckItemDetailsColumnVisibility;
  });

  // Save column visibility states to localStorage
  useEffect(() => {
    localStorage.setItem('bin-adjustment-column-visibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-adjustment-details-column-visibility', JSON.stringify(detailsColumnVisibility));
  }, [detailsColumnVisibility]);

  useEffect(() => {
    localStorage.setItem('bin-adjustment-item-data-column-visibility', JSON.stringify(itemDataColumnVisibility));
  }, [itemDataColumnVisibility]);



  useEffect(() => {
    localStorage.setItem('bin-adjustment-ack-item-details-column-visibility', JSON.stringify(ackItemDetailsColumnVisibility));
  }, [ackItemDetailsColumnVisibility]);

  // Save date range and success filter to localStorage
  useEffect(() => {
    localStorage.setItem('bin-adjustment-start-date', startDate);
  }, [startDate]);

  useEffect(() => {
    localStorage.setItem('bin-adjustment-end-date', endDate);
  }, [endDate]);

  useEffect(() => {
    localStorage.setItem('bin-adjustment-success-filter', successFilter);
  }, [successFilter]);

  // Process the data to handle any necessary transformations
  const processedData = useMemo(() => {
    if (!data) return [];
    
    let rawData = [];
    
    // If data is an array, use it directly
    if (Array.isArray(data)) {
      rawData = data;
    }
    // If data has a data property that's an array, use that
    else if (data.data && Array.isArray(data.data)) {
      rawData = data.data;
    }
    // If data is an object, convert it to an array
    else if (typeof data === 'object') {
      rawData = Object.values(data);
    }
    
    // Process each record to extract username and handle transformations
    return rawData.map(record => {
      let username = '';
      
      // Enhanced username extraction from various possible locations in the data
      if (record.username) {
        username = record.username;
      } else if (record.user_data && record.user_data.username) {
        username = record.user_data.username;
      } else if (record.bin_data && record.bin_data.username) {
        username = record.bin_data.username;
      } else if (record.item_data && record.item_data.username) {
        username = record.item_data.username;
      } else if (record.netsuite_response && record.netsuite_response.username) {
        username = record.netsuite_response.username;
      } else if (record.user && record.user.username) {
        username = record.user.username;
      } else if (record.user && typeof record.user === 'string') {
        username = record.user;
      } else if (record.user_id) {
        username = record.user_id;
      } else if (record.userid) {
        username = record.userid;
      } else if (record.created_by) {
        username = record.created_by;
      } else if (record.modified_by) {
        username = record.modified_by;
      } else if (record.operator) {
        username = record.operator;
      } else if (record.operator_id) {
        username = record.operator_id;
      } else if (record.operatorid) {
        username = record.operatorid;
      }
      
      // Deep search for username in nested objects if still not found
      if (!username) {
        const findUsernameInObject = (obj, path = '') => {
          if (!obj || typeof obj !== 'object') return null;
          
          for (const [key, value] of Object.entries(obj)) {
            const currentPath = path ? `${path}.${key}` : key;
            
            // Check if this key contains username-related terms
            if (key.toLowerCase().includes('user') || key.toLowerCase().includes('operator') || key.toLowerCase().includes('created_by') || key.toLowerCase().includes('modified_by')) {
              if (typeof value === 'string' && value.trim()) {
                return value.trim();
              } else if (value && typeof value === 'object' && value.username) {
                return value.username;
              }
            }
            
            // Recursively search nested objects (but avoid infinite recursion)
            if (value && typeof value === 'object' && !Array.isArray(value) && currentPath.split('.').length < 3) {
              const found = findUsernameInObject(value, currentPath);
              if (found) return found;
            }
          }
          return null;
        };
        
        const foundUsername = findUsernameInObject(record);
        if (foundUsername) {
          username = foundUsername;
        }
      }
      
      // Debug logging for records without username
      if (!username && record.id) {
        console.log('No username found for record ID:', record.id, 'Available fields:', Object.keys(record));
      }
      
      return {
        ...record,
        username // Add username to main table
      };
    }).sort((a, b) => {
      // Sort by created_at timestamp in descending order (newest first)
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA;
    });
  }, [data]);

  // Apply success filter (All / Success / Errors)
  const filteredData = useMemo(() => {
    if (!processedData.length) {
      return [];
    }
    if (successFilter === 'all') {
      return processedData;
    }

    const filtered = processedData.filter((record) => {
      const success = record?.netsuite_response?.netsuite_acknowledgment?.success;
      const isSuccess =
        success === true || success === 'Yes' || success === 'yes' || success === 1 || success === '1';
      const isFailure =
        success === false || success === 'No' || success === 'no' || success === 0 || success === '0';

      if (successFilter === 'success') {
        return isSuccess;
      }
      if (successFilter === 'errors') {
        return isFailure;
      }
      return true;
    });
    return filtered;
  }, [processedData, successFilter]);

  // Generate columns dynamically with fixed order
  const columns = useMemo(() => {
    if (!processedData.length) {
      return [];
    }

    const baseColumns = [];

    // 1. ID column
    baseColumns.push({
      accessorKey: 'id',
      header: 'ID',
      enableSorting: true
    });

    // 2. Username column
    baseColumns.push({
      accessorKey: 'username',
      header: 'Username',
      enableSorting: true,
      cell: info => {
        const value = info.getValue();
        return value || '-';
      }
    });

    // 3. Bin Name column (if exists)
    if (processedData[0].bin_name) {
      baseColumns.push({
        accessorKey: 'bin_name',
        header: 'Bin Name',
        enableSorting: true,
        cell: info => info.getValue()
      });
    }



    // 5. Inventory ID column (if exists)
    if (processedData[0].inventory_id) {
      baseColumns.push({
        accessorKey: 'inventory_id',
        header: 'Inventory ID',
        enableSorting: true,
        cell: info => info.getValue()
      });
    }

    // 6. Bin ID column (if exists)
    if (processedData[0].bin_id) {
      baseColumns.push({
        accessorKey: 'bin_id',
        header: 'Bin ID',
        enableSorting: true,
        cell: info => info.getValue()
      });
    }

    // 7. Bin Data column with status indicator
    if (processedData[0].bin_data) {
      baseColumns.push({
        accessorKey: 'bin_data',
        header: 'Bin Data',
        enableSorting: true,
        cell: info => {
          const value = info.getValue();
          if (!value) return '-';
          
          const hasData = Array.isArray(value) ? value.length > 0 : (value && typeof value === 'object' && Object.keys(value).length > 0);
          let success;
          let original;
          try {
            original = info.row?.original;
            success = original?.netsuite_response?.netsuite_acknowledgment?.success;
          } catch {}
          
          // Handle both boolean and string values for success
          const isSuccess = success === true || success === 'Yes' || success === 'yes';
          const isFailure = success === false || success === 'No' || success === 'no';
          
          // If we have NetSuite response data but success is undefined, show as failure (red dot)
          const hasNetSuiteResponse = original?.netsuite_response;
          const shouldShowFailure = isFailure || (hasNetSuiteResponse && success === undefined);
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: isSuccess ? '#4caf50' : (shouldShowFailure ? '#e53935' : 'transparent') }} />
              <Typography variant="body2">{hasData ? 'View Details' : 'No Data'}</Typography>
            </Box>
          );
        }
      });
    }

    // 8. Created At column (if exists)
    if (processedData[0].created_at) {
      baseColumns.push({
        accessorKey: 'created_at',
        header: 'Created At',
        enableSorting: true,
        cell: info => {
          const value = info.getValue();
          if (!value) return '-';
          try {
            const date = new Date(value);
            return date.toLocaleString();
          } catch (e) {
            return value;
          }
        }
      });
    }

    // 9. Updated At column (if exists)
    if (processedData[0].updated_at) {
      baseColumns.push({
        accessorKey: 'updated_at',
        header: 'Updated At',
        enableSorting: true,
        cell: info => {
          const value = info.getValue();
          if (!value) return '-';
          try {
            const date = new Date(value);
            return date.toLocaleString();
          } catch (e) {
            return value;
          }
        }
      });
    }

    // Add any remaining columns that weren't in the fixed order
    const allKeys = Object.keys(processedData[0]);
    const fixedOrderKeys = ['id', 'username', 'bin_name', 'netsuite_response', 'inventory_id', 'bin_id', 'bin_data', 'created_at', 'updated_at'];
    
    allKeys.forEach(key => {
      if (!fixedOrderKeys.includes(key)) {
        const columnExists = baseColumns.some(col => col.accessorKey === key);
        if (!columnExists) {
          const baseColumn = {
            accessorKey: key,
            header: formatColumnHeader(key),
            enableSorting: true,
          };

          // Special handling for nested data that we want to show in detail view
          if (
            typeof processedData[0][key] === 'object' && 
            processedData[0][key] !== null && 
            !Array.isArray(processedData[0][key]) &&
            Object.keys(processedData[0][key]).length > 0
          ) {
            baseColumns.push({
              ...baseColumn,
              cell: info => {
                const value = info.getValue();
                if (!value) return '-';
                
                return (
                  <Button
                    variant={BUTTON_VARIANT}
                    size={BUTTON_SIZE}
                    sx={BUTTON_STYLE}
                    onClick={() => {
                      setSelectedDetails(value);
                      setDetailsOpen(true);
                    }}
                  >
                    View Details
                  </Button>
                );
              }
            });
          } else {
            // Regular column handling
            baseColumns.push({
              ...baseColumn,
              cell: info => {
                const value = info.getValue();
                
                if (value === null || value === undefined) {
                  return '-';
                }
                
                // Handle date fields
                if (key.toLowerCase().includes('date') && value) {
                  try {
                    const date = new Date(value);
                    if (!isNaN(date)) {
                      return date.toLocaleString();
                    }
                  } catch (e) {
                    return formatValue(value);
                  }
                }
                
                // Handle boolean values
                if (typeof value === 'boolean') {
                  return value ? 'Yes' : 'No';
                }
                
                // Handle objects and arrays
                if (typeof value === 'object') {
                  return formatValue(value);
                }
                
                return value;
              }
            });
          }
        }
      }
    });

    return baseColumns;
  }, [processedData]);

  // Generate detail columns dynamically
  const detailColumns = useMemo(() => {
    if (!selectedDetails) return [];
    
    const flattenedData = flattenObject(selectedDetails);
    const keys = Object.keys(flattenedData).filter(key => key !== 'username');
    
    return keys.map(key => {
      // Determine appropriate column size based on key name
      let columnSize = 200;
      let minSize = 150;
      
      if (key.toLowerCase().includes('id')) {
        columnSize = 180;
        minSize = 150;
      } else if (key.toLowerCase().includes('name') || key.toLowerCase().includes('description')) {
        columnSize = 400;
        minSize = 300;
      } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        columnSize = 250;
        minSize = 200;
      } else if (key.toLowerCase().includes('quantity') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('count')) {
        columnSize = 180;
        minSize = 150;
      } else if (key.toLowerCase().includes('status') || key.toLowerCase().includes('type')) {
        columnSize = 200;
        minSize = 150;
      } else if (key.toLowerCase().includes('code') || key.toLowerCase().includes('sku')) {
        columnSize = 250;
        minSize = 200;
      } else if (key.toLowerCase().includes('price') || key.toLowerCase().includes('cost')) {
        columnSize = 200;
        minSize = 150;
      }
      
      const baseColumn = {
        accessorKey: key,
        header: formatColumnHeader(key),
        enableSorting: true,
      };

      // Special handling for itemData arrays
      if ((key === 'itemData' || key.endsWith('_itemData')) && Array.isArray(flattenedData[key])) {
        return {
          ...baseColumn,
          cell: info => {
            const value = info.getValue();
            if (!value || !Array.isArray(value)) return '-';
            
            return `${value.length} items`;
          }
        };
      }

      // Regular column handling
      return {
        ...baseColumn,
        cell: info => {
          const value = info.getValue();
          
          if (value === null || value === undefined) {
            return '-';
          }
          
          if (key.toLowerCase().includes('date') && value) {
            try {
              const date = new Date(value);
              if (!isNaN(date)) {
                return date.toLocaleString();
              }
            } catch (e) {
              return formatValue(value);
            }
          }
          
          if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
          }
          
          if (typeof value === 'object') {
            return formatValue(value);
          }
          
          return value;
        }
      };
    });
  }, [selectedDetails]);

  // Generate item data columns dynamically
  const itemDataColumns = useMemo(() => {
    if (!itemDataDetails || !itemDataDetails.length) return [];
    
    // Get all unique keys from the first item, excluding username
    const keys = Object.keys(itemDataDetails[0]).filter(key => key !== 'username');
    
    return keys.map(key => {
      // Determine appropriate column size based on key name
      let columnSize = 200;
      let minSize = 150;
      
      if (key.toLowerCase().includes('id')) {
        columnSize = 180;
        minSize = 150;
      } else if (key.toLowerCase().includes('name') || key.toLowerCase().includes('description')) {
        columnSize = 400;
        minSize = 300;
      } else if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        columnSize = 250;
        minSize = 200;
      } else if (key.toLowerCase().includes('quantity') || key.toLowerCase().includes('amount') || key.toLowerCase().includes('count')) {
        columnSize = 180;
        minSize = 150;
      } else if (key.toLowerCase().includes('status') || key.toLowerCase().includes('type')) {
        columnSize = 200;
        minSize = 150;
      } else if (key.toLowerCase().includes('code') || key.toLowerCase().includes('sku')) {
        columnSize = 250;
        minSize = 200;
      }
      
      return {
        accessorKey: key,
        header: formatColumnHeader(key),
        enableSorting: true,
        cell: info => {
          const value = info.getValue();
          
          if (value === null || value === undefined) {
            return '-';
          }
          
          if (key.toLowerCase().includes('date') && value) {
            try {
              const date = new Date(value);
              if (!isNaN(date)) {
                return date.toLocaleString();
              }
            } catch (e) {
              return formatValue(value);
            }
          }
          
          if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
          }
          
          if (typeof value === 'object') {
            return formatValue(value);
          }
          
          return value;
        }
      };
    });
  }, [itemDataDetails]);

  const binAdjustmentAckItemColumns = useMemo(
    () => [
      { accessorKey: 'item', header: 'Item'},
      { accessorKey: 'status', header: 'Status'},
      { accessorKey: 'on_hand', header: 'On Hand'},
      { accessorKey: 'location', header: 'Location'},
      { accessorKey: 'available', header: 'Available'},
      { accessorKey: 'bin_number', header: 'Bin Number'},
      { accessorKey: 'bin_internal_id', header: 'Bin Internal ID'},
      { accessorKey: 'inventory_number', header: 'Inventory Number'},
      { accessorKey: 'item_internal_id', header: 'Item Internal ID'},
    ],
    []
  );

  // Handle refresh click
  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await refetch();
      setSnackbar({
        open: true,
        message: 'Data refreshed successfully',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to refresh data',
        severity: 'error'
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const binAdjustmentMainGrid = useDataGridProTable({
    data: filteredData,
    columns,
    loading: isLoading,
    error: error ? (error.message || 'Failed to load bin adjustment records') : null,
    title: 'Bin Adjustment Records',
    initialColumnVisibility: columnVisibility,
    onColumnVisibilityChange: setColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    onRowClick: (row) => {
      if (!row || !row.bin_data) return;
      setDetailsOpen(true);
      setDetailsLoading(true);
      setTimeout(() => {
        setSelectedDetails(row.bin_data);
        setDetailsLoading(false);
      }, 100);
    },
    muiTableContainerProps: { sx: {} },
    initialSorting: [{ id: 'created_at', desc: true }],
    onDataRefresh: handleRefreshClick,
    actionButtons: [
      <Box key="bin-adjustment-filters" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Tooltip title="Refresh Data">
          <IconButton
            color="primary"
            onClick={handleRefreshClick}
            disabled={isRefreshing}
            sx={{ ml: 1 }}
          >
            {isRefreshing ? (
              <CircularProgress size={20} />
            ) : (
              <RefreshIcon />
            )}
          </IconButton>
        </Tooltip>
        <TextField
          select
          label="View"
          size="small"
          value={successFilter}
          onChange={(e) => setSuccessFilter(e.target.value)}
          sx={{
            minWidth: 140,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        >
          <MenuItem value="all" sx={{ fontSize: '0.8125rem' }}>All</MenuItem>
          <MenuItem value="success" sx={{ fontSize: '0.8125rem' }}>Success</MenuItem>
          <MenuItem value="errors" sx={{ fontSize: '0.8125rem' }}>Errors</MenuItem>
        </TextField>
        <TextField
          label="Start Date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getDateInputProps()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 150,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        />
        <TextField
          label="End Date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={getDateInputProps()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon sx={{ fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: 150,
            '& .MuiInputBase-root': { fontSize: '0.8125rem', height: '32px' },
            '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
          }}
        />
      </Box>,
    ],
  });

  const binAdjustmentDetailsGrid = useDataGridProTable({
    data: selectedDetails ? [selectedDetails] : [],
    title: 'Details',
    columns: detailColumns,
    initialSorting: [{ id: 'id', desc: false }],
    initialColumnVisibility: detailsColumnVisibility,
    onColumnVisibilityChange: setDetailsColumnVisibility,
    onRowClick: (row) => {
      if (!row) return;
      const value =
        row.itemData ||
        row.ItemData ||
        row.item_data ||
        row.original?.itemData ||
        row.original?.ItemData ||
        row.original?.item_data;
      if (Array.isArray(value) && value.length > 0) {
        setItemDataOpen(true);
        setItemDataLoading(true);
        setTimeout(() => {
          setItemDataDetails(value);
          setItemDataLoading(false);
        }, 100);
      }
    },
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    loading: false,
    error: null,
  });

  const binAdjustmentItemDataGrid = useDataGridProTable({
    data: itemDataDetails || [],
    title: 'Item Data Details',
    columns: itemDataColumns,
    initialSorting: [{ id: 'id', desc: false }],
    initialColumnVisibility: itemDataColumnVisibility,
    onColumnVisibilityChange: setItemDataColumnVisibility,
    onRowClick: (row) => {
      if (!row) return;
      const value =
        row.itemData ||
        row.ItemData ||
        row.item_data ||
        row.original?.itemData ||
        row.original?.ItemData ||
        row.original?.item_data;
      if (Array.isArray(value) && value.length > 0) {
        setItemDataOpen(true);
        setItemDataLoading(true);
        setTimeout(() => {
          setItemDataDetails(value);
          setItemDataLoading(false);
        }, 100);
      }
    },
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    loading: false,
    error: null,
  });

  const binAdjustmentAckGrid = useDataGridProTable({
    data: ackItemDetails || [],
    title: 'NetSuite Item Details',
    columns: binAdjustmentAckItemColumns,
    initialSorting: [{ id: 'item', desc: false }],
    initialColumnVisibility: ackItemDetailsColumnVisibility,
    onColumnVisibilityChange: setAckItemDetailsColumnVisibility,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enablePinning: true,
    muiTableContainerProps: { sx: { overflow: 'auto' } },
    disablePaper: true,
    hideHeader: false,
    loading: false,
    error: null,
  });

  return (
    <DashboardLayout>
      <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <MuiXDataGridSurface hook={binAdjustmentMainGrid} DataGridPro={DataGridPro} />
      </Box>

      {/* Details Dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)} 
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          Record Details
          <IconButton
            aria-label="close"
            onClick={() => setDetailsOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {detailsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : selectedDetails && (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {/* NetSuite Response Details */}
              {(() => {
                // Get the NetSuite response from the main record that was clicked
                const mainRecord = processedData.find(record => 
                  record.bin_data === selectedDetails || 
                  (Array.isArray(record.bin_data) && record.bin_data.length > 0 && record.bin_data[0] === selectedDetails[0])
                );
                
                if (!mainRecord) return null;
                
                const nsObject = mainRecord.netsuite_response;
                if (!nsObject) return null;
                
                const ns = nsObject.netsuite_response || nsObject;
                const ack = ns?.netsuite_acknowledgment || {};
                const safe = (v) => {
                  if (v === null || v === undefined) return '-';
                  if (typeof v === 'object') return JSON.stringify(v);
                  return String(v);
                };
                return (
                  <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, flexShrink: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      NetSuite Response Details
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '220px 1fr' }, rowGap: 0.5, columnGap: 2 }}>
                      <Typography variant="body2" color="text.secondary">Message</Typography>
                      <Typography variant="body2">{safe(ns?.message)}</Typography>
                      <Typography variant="body2" color="text.secondary">Operation Time</Typography>
                      <Typography variant="body2">{safe(ns?.operation_time)}</Typography>
                      <Typography variant="body2" color="text.secondary">Success</Typography>
                      <Typography variant="body2">{ack?.success ? 'Yes' : 'No'}</Typography>
                      {ack?.inventoryAdjustmentId && (
                        <>
                          <Typography variant="body2" color="text.secondary">Inventory Adjustment ID</Typography>
                          <Typography variant="body2">{safe(ack.inventoryAdjustmentId)}</Typography>
                        </>
                      )}
                      {ack?.binTransferId && (
                        <>
                          <Typography variant="body2" color="text.secondary">Bin Transfer ID</Typography>
                          <Typography variant="body2">{safe(ack.binTransferId)}</Typography>
                        </>
                      )}
                    </Box>
                    {Array.isArray(ack?.itemDetails) && ack.itemDetails.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          variant={BUTTON_VARIANT}
                          size={BUTTON_SIZE}
                          sx={BUTTON_STYLE}
                          onClick={() => {
                            // Open dialog immediately
                            setItemDataOpen(true);
                            setItemDataLoading(true);
                            
                            // Set data after a brief delay to show loading spinner
                            setTimeout(() => {
                              setItemDataDetails(ack.itemDetails);
                              setItemDataLoading(false);
                            }, 100);
                          }}
                        >
                          View Items ({ack.itemDetails.length})
                        </Button>
                      </Box>
                    )}
                  </Box>
                );
              })()}
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                <MuiXDataGridSurface hook={binAdjustmentDetailsGrid} DataGridPro={DataGridPro} />
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Item Data Dialog */}
      <Dialog 
        open={itemDataOpen} 
        onClose={() => setItemDataOpen(false)} 
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          Item Data Details
          <IconButton
            aria-label="close"
            onClick={() => setItemDataOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {itemDataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <CircularProgress />
            </Box>
          ) : itemDataDetails && itemDataDetails.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={binAdjustmentItemDataGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            'No item data available.'
          )}
        </DialogContent>
      </Dialog>



      {/* Item Details from NetSuite Ack Dialog */}
      <Dialog 
        open={ackItemDetailsOpen} 
        onClose={() => setAckItemDetailsOpen(false)} 
        maxWidth="xl"
        fullWidth
        disableEnforceFocus
        keepMounted
        PaperProps={{ sx: NEAR_FULLSCREEN_DIALOG_PAPER_SX }}
      >
        <DialogTitle>
          NetSuite Item Details
          <IconButton
            aria-label="close"
            onClick={() => setAckItemDetailsOpen(false)}
            sx={DIALOG_CLOSE_BUTTON_STYLE}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, p: 1, overflow: 'hidden' }}>
          {ackItemDetails && ackItemDetails.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <MuiXDataGridSurface hook={binAdjustmentAckGrid} DataGridPro={DataGridPro} />
            </Box>
          ) : (
            'No item details available.'
          )}
        </DialogContent>
      </Dialog>

      {/* Add Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default BinAdjustment; 